// @/components/reusable/animations.js

/**
 * V2 Animation System — Single source of truth for all motion config.
 *
 * Golden spring identified from ActivityCardEmpty (best-feeling animation
 * on the app). All page-level, card, and banner animations derive from this.
 *
 * Accessibility:
 *   MotionConfig reducedMotion="user" is set at the app root (gatsby-browser.js).
 *   When the OS preference is "reduce motion", transform/layout animations are
 *   automatically disabled while opacity fades are preserved.
 *
 * @module components/reusable/animations
 */

// ─── Golden Spring ─────────────────────────────────────────────────────────────
// The single spring for all standard UI transitions (cards, banners, sections).
export const spring = {
  type: "spring",
  damping: 26,
  stiffness: 300,
  mass: 0.5,
  bounce: 0,
  visualDuration: 0.25,
};

// ─── Derivative Springs ────────────────────────────────────────────────────────
/** Snappier spring for dropdowns and popovers */
export const dropdownSpring = { type: "spring", damping: 25, stiffness: 400, mass: 0.4 };

/** Value counting animation (AnimateNumber, counters) */
export const valueSpring = { type: "spring", damping: 26, stiffness: 300, mass: 0.5 };

// ─── Timed Transitions ─────────────────────────────────────────────────────────
/** Quick exit for overlays and menus */
export const quickExit = { duration: 0.1 };

/** Fade transition for processing overlays */
export const overlayFade = { duration: 0.2 };

/** Slow easing for chart bar/counter animations — 0.7s with custom cubic-bezier */
export const chartTransition = { duration: 0.7, ease: [0.16, 1, 0.3, 1] };

// ─── Animation Presets ─────────────────────────────────────────────────────────
// Spread these directly onto motion.div: <motion.div {...INTRO}>

/** Standard intro — fade in + slide up 5px with golden spring */
export const INTRO = {
  initial: { opacity: 0, y: 5 },
  animate: { opacity: 1, y: 0 },
  transition: spring,
};

/** Simple opacity fade */
export const FADE = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
  transition: { duration: 0.15 },
};

/** Quick exit — slide up 4px + fade out (for AnimatePresence exit) */
export const EXIT = { opacity: 0, y: -4, transition: { duration: 0.15 } };

/** Tab content swap — fast exit + snappy spring intro so total ≈ intro-only duration */
export const TABS = {
  initial: { opacity: 0, y: 8 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -4, transition: { duration: 0.06 } },
  transition: { type: "spring", damping: 28, stiffness: 400, mass: 0.2, bounce: 0 },
};

// ─── Dialog & Drawer Presets ──────────────────────────────────────────────────
// Only the backdrop and panel animate. Content inside is static — the panel
// animation is enough. Keeps things simple and consistent.

export const DIALOG_BACKDROP = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
  transition: overlayFade,
};

export const DIALOG_PANEL = {
  initial: { opacity: 0, scale: 0.95, y: 10 },
  animate: { opacity: 1, scale: 1, y: 0 },
  exit: { opacity: 0, scale: 0.98, y: 5 },
  transition: spring,
};

// ─── Drawer Presets ─────────────────────────────────────────────────────────
// Side-panel drawers use the golden spring for consistency with the rest of the app.

export const DRAWER_BACKDROP = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
  transition: overlayFade,
};

export const DRAWER_PANEL_RIGHT = {
  initial: { x: 8, opacity: 0 },
  animate: { x: 0, opacity: 1 },
  exit: { x: 8, opacity: 0 },
  transition: spring,
};

export const DRAWER_PANEL_LEFT = {
  initial: { x: -8, opacity: 0 },
  animate: { x: 0, opacity: 1 },
  exit: { x: -8, opacity: 0 },
  transition: spring,
};

// ─── Stagger Helper ────────────────────────────────────────────────────────────
/**
 * Calculate stagger delay for sequential element entrance.
 *
 * @param {number} index  — 0-based position in the sequence
 * @param {number} [base=0.05] — delay increment per step (seconds)
 * @returns {number} delay in seconds
 *
 * @example
 * // Three sections entering in sequence:
 * <motion.div {...INTRO} transition={{ ...spring, delay: stagger(0) }}>
 * <motion.div {...INTRO} transition={{ ...spring, delay: stagger(1) }}>
 * <motion.div {...INTRO} transition={{ ...spring, delay: stagger(2) }}>
 */
export const stagger = (index, base = 0.05) => base * index;
